import { LOGO } from '@/assets/images'
import { EyeOffSvgIcon, EyeSvgIcon } from '@/assets/svg'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useLoginMutation } from '@/redux/services/auth/authApi'
import routesPath from '@/utils/routesPath'
import { loginSchema } from '@/utils/schemas/auth'
import { useFormik } from 'formik'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

const { FORGOT_PASSWORD, DASHBOARD } = routesPath

interface LoginFormValues {
  email: string
  password: string
}

const Login = () => {
  const [showPassword, setShowPassword] = useState(false)

  const [login, { isLoading }] = useLoginMutation()

  const navigate = useNavigate()

  const handleSubmit = (values: LoginFormValues) => {
    login(values)
      .unwrap()
      .then((res: { success: boolean; message: string }) => {
        if (res?.success === true) {
          navigate(DASHBOARD)
          toast.success(res?.message)
        } else {
          toast.error(res?.message)
        }
      })
      .catch((error) => {
        const errorMessage =
          (error as { data?: { message?: string } })?.data?.message ||
          'Unable to log in. Please try again.'
        toast.error(errorMessage)
      })
  }

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: loginSchema,
    onSubmit: handleSubmit,
  })

  return (
    <div className='min-h-screen bg-[#F9F9F9] flex sm:items-center sm:justify-center p-4'>
      <div className=' sm:bg-[#FFFFFF] sm:rounded-lg sm:shadow-sm px-4 pt-10 sm:p-8 w-full max-w-[793px] sm:px-[155px] sm:py-[64px]'>
        <div className='flex justify-center mb-6'>
          <img
            src={LOGO}
            alt='logo'
            className='h-[30px] w-[136px] md:w-[179px] max-w-[179px] md:h-[43px] md:max-h-[43px]'
          />
        </div>

        <h1 className='text-[clamp(1.25rem,4vw,2.125rem)] w-[261px]  md:text-[34px] font-semibold text-[#4C4C4C] mb-8 md:w-[364px]'>
          Welcome to Gloopro Venda
        </h1>

        <form onSubmit={formik.handleSubmit} className=''>
          <div className='space-y-[23px]'>
            <div>
              <Input
                id='email'
                name='email'
                label='Email/Phone number'
                type='text'
                placeholder='Enter email address or phone number'
                value={formik.values.email}
                onChange={formik.handleChange}
                containerClass=''
                error={formik.errors.email}
                touched={formik.touched.email}
              />
            </div>
            <div className='relative'>
              <Input
                id='password'
                name='password'
                label='Password'
                type={showPassword ? 'text' : 'password'}
                placeholder='Enter 8 character password'
                value={formik.values.password}
                onChange={formik.handleChange}
                error={formik.errors.password}
                touched={formik.touched.password}
                containerClass=''
              />
              <button
                type='button'
                className='absolute right-3 top-8 md:top-10.5 text-gray-400 hover:text-gray-600 cursor-pointer'
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeSvgIcon className='w-[16px] h-[16px] text-gray-400 hover:text-gray-600' />
                ) : (
                  <EyeOffSvgIcon className='w-[16px] h-[16px] text-gray-400 hover:text-gray-600' />
                )}
              </button>
            </div>

            <Button
              type='submit'
              className='w-full hover:bg-primary/90 text-white font-medium py-3 rounded-lg'
              disabled={isLoading}
              loading={isLoading}
              // loadingText='Logging in...'
            >
              Login
            </Button>
          </div>

          <div
            className='text-sm mt-2'
            onClick={() => navigate(FORGOT_PASSWORD)}
          >
            <span className='text-gray-600'>
              Forgot password?{' '}
              <button
                type='button'
                className='text-gray-800 hover:text-green-700 font-semibold hover:underline cursor-pointer'
              >
                Reset
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
  )
}

export default Login
